import { CalendarTimeEntry, CalendarMonthData } from '../../services/types';
import {
  formatDisplayDate,
  formatHours,
  getCompanyColor,
  formatDateKey,
  normalizeCompanyName,
  getTotalHours,
} from './calendarUtils';

interface DayViewProps {
  currentDate: Date;
  monthData: CalendarMonthData | null;
  selectedCompanies: Set<string>;
  searchFilter?: (entry: CalendarTimeEntry) => boolean;
  onEntryClick: (entry: CalendarTimeEntry, event: React.MouseEvent) => void;
  onAddEntry: (date: Date) => void;
}

export default function DayView({
  currentDate,
  monthData,
  selectedCompanies,
  searchFilter,
  onEntryClick,
  onAddEntry,
}: DayViewProps) {
  const getEntriesForDate = (date: Date): CalendarTimeEntry[] => {
    if (!monthData?.byDate) return [];
    const key = formatDateKey(date);
    const entries = monthData.byDate[key] || [];
    if (selectedCompanies.size === 0) return [];
    return entries
      .filter(e => selectedCompanies.has(normalizeCompanyName(e.companyName)))
      .filter(e => !searchFilter || searchFilter(e));
  };

  const entries = getEntriesForDate(currentDate);
  const totalHours = getTotalHours(entries);
  const today = new Date();
  const isToday = currentDate.toDateString() === today.toDateString();

  return (
    <div className="flex flex-col h-full">
      {/* Day Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-12 h-12 rounded-full text-xl font-semibold ${
            isToday ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300'
          }`}>
            {currentDate.getDate()}
          </div>
          <div>
            <h2 className="text-lg font-medium text-white">
              {formatDisplayDate(currentDate)}
            </h2>
            <p className="text-sm text-slate-400">
              {entries.length} {entries.length === 1 ? 'entry' : 'entries'} &middot; {formatHours(totalHours)}
            </p>
          </div>
        </div>
        <button
          onClick={() => onAddEntry(currentDate)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Entry
        </button>
      </div>

      {/* Entries List - click blank space to add entry */}
      <div
        className="flex-1 overflow-y-auto p-4 cursor-pointer"
        onClick={() => onAddEntry(currentDate)}
      >
        {entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <svg className="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg">No time entries for this day</p>
            <p className="mt-2 text-sm text-slate-600">Click anywhere to add an entry</p>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry, index) => {
              const color = getCompanyColor(entry.companyName);
              return (
                <button
                  key={entry.id || index}
                  onClick={(e) => { e.stopPropagation(); onEntryClick(entry, e); }}
                  className="w-full text-left p-3 rounded-lg bg-slate-700/60 hover:bg-slate-700 border border-slate-600/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {/* Color indicator bar */}
                    <div className={`w-1 h-10 rounded-full ${color.bg} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{entry.companyName}</div>
                      <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">{entry.summaryNotes}</div>
                    </div>
                    <div className="text-sm font-semibold text-slate-300 flex-shrink-0">{formatHours(entry.hoursWorked)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
